import { lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, RequireAuth } from './auth'
import { RequireAdmin } from '../shared/components/RequireAdmin'
import DashboardLayout from '../shared/layouts/DashboardLayout'

// Login is eagerly loaded (entry point)
import Login from '../features/dashboard/Login'

// Build mode determines which product this bundle serves.
// 'ops' and 'fsru' produce single-product bundles for separate domains.
// 'all' includes both modules + the Home selector (used in dev).
const MODE = __APP_MODE__

// All other pages are lazy-loaded for code splitting
const Home = lazy(() => import('../features/home/Home'))
const Dashboard = lazy(() => import('../features/dashboard/Dashboard'))
const LiveView = lazy(() => import('../features/vessels/LiveView'))
const Vessels = lazy(() => import('../features/vessels/Vessels'))
const VoyagePerformance = lazy(() => import('../features/voyage-performance/VoyagePerformance'))
const BOGManagement = lazy(() => import('../features/bog-management/BOGManagement'))
const VesselReports = lazy(() => import('../features/vessel-reports/VesselReports'))
const Graphs = lazy(() => import('../features/graphs/Graphs'))
const Reports = lazy(() => import('../features/reports/Reports'))
const Scheduler = lazy(() => import('../features/scheduler/Scheduler'))
const SSCS = lazy(() => import('../features/sscs/SSCS'))
const Calculators = lazy(() => import('../features/calculators/Calculators'))
const Admin = lazy(() => import('../features/admin/Admin'))
const Messages = lazy(() => import('../features/messages/Messages'))
const Charterers = lazy(() => import('../features/charterers/Charterers'))
const Itinerary = lazy(() => import('../features/itinerary/Itinerary'))
const FSRUModule = lazy(() => import('../features/fsru/FSRU'))

// Where the root URL "/" should land for each build mode
const ROOT_REDIRECT: Record<typeof MODE, string> = {
  ops: '/lng-ops',
  fsru: '/fsru',
  all: '/home',
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Home — only included in 'all' build (dev). Single-product builds skip it. */}
        {MODE === 'all' && (
          <Route
            path="/home"
            element={
              <RequireAuth>
                <Home />
              </RequireAuth>
            }
          />
        )}

        {/* LNG Operations module — only in 'ops' or 'all' builds */}
        {MODE !== 'fsru' && (
          <Route
            path="/lng-ops"
            element={
              <RequireAuth>
                <DashboardLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="live-view" element={<LiveView />} />
            <Route path="itinerary" element={<Itinerary />} />
            <Route path="vessels" element={<Vessels />} />
            <Route path="voyage-performance" element={<VoyagePerformance />} />
            <Route path="bog-management" element={<BOGManagement />} />
            <Route path="vessel-reports" element={<VesselReports />} />
            <Route path="sscs" element={<SSCS />} />
            <Route path="calculators" element={<Calculators />} />
            <Route path="graphs" element={<Graphs />} />
            <Route path="reports" element={<Reports />} />
            <Route path="scheduler" element={<Scheduler />} />
            <Route path="messages" element={<Messages />} />
            <Route path="charterers" element={<Charterers />} />
            <Route path="admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          </Route>
        )}

        {/* FSRU module — only in 'fsru' or 'all' builds */}
        {MODE !== 'ops' && (
          <Route
            path="/fsru"
            element={
              <RequireAuth>
                <DashboardLayout />
              </RequireAuth>
            }
          >
            <Route index element={<FSRUModule />} />
            <Route path="regasification" element={<FSRUModule />} />
            <Route path="cargo" element={<FSRUModule />} />
            <Route path="quality" element={<FSRUModule />} />
            <Route path="performance" element={<FSRUModule />} />
            <Route path="sts" element={<FSRUModule />} />
          </Route>
        )}

        {/* Root + 404 fall through to the appropriate landing page for this build */}
        <Route path="/" element={<Navigate to={ROOT_REDIRECT[MODE]} replace />} />
        <Route path="*" element={<Navigate to={ROOT_REDIRECT[MODE]} replace />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
